import { useMutation, useQuery } from "@apollo/client";
import { signOut as signOutAuth } from "aws-amplify/auth";
import { PropsWithChildren, createContext, useEffect, useMemo, useState } from "react";
import { configureSentryUser } from "sentry";

import { gql } from "../generated-graphql";
import { GetUserQuery, GetUserQueryVariables, UserType } from "../generated-graphql/graphql";

export const LAST_SSO_LOGIN_KEY = "lastSsoLogin";
export const ENCAMP_SSO_PROVIDER = "Google";

export type CurrentUser = GetUserQuery["getCurrentUser"];

type CurrentUserProviderContext = {
  user: CurrentUser | undefined;
  loading: boolean;
  refetchUser: () => void;
};

export type LastSsoLogin = {
  email: string;
  provider: string;
};

export const currentUserContext = createContext<CurrentUserProviderContext>({
  user: undefined,
  loading: true,
  refetchUser: () => {},
});

export function checkIsOriginalUserStaff(user?: CurrentUser): boolean {
  return !!user && (user.originalUserType ?? user.type) !== UserType.Customer;
}

export function isEncampSSOEmail(email?: string): boolean {
  if (!email) return false;

  const [emailLocalPart, emailDomain] = email.split("@");

  return emailDomain?.toLowerCase() === "encamp.com" && !emailLocalPart.includes("+");
}

const GET_USER_QUERY = gql(`
  query GetUser {
    getCurrentUser {
      id
      email
      type
      isPartner
      person {
        id
        first
        last
      }
      UserTenant {
        tenantId
        role {
          name
          permissions
        }
        tenant {
          id
          name
          partnerTenant {
            id
            name
            ssoMappingName
            ssoDomains
          }
          partnersTenants {
            id
          }
          ssoMappingName
          ssoDomains
        }
      }
      facilities {
        id
      }
      isEmulating
      originalUserType
    }
  }
`);

const CREATE_USER_LOGIN = gql(`
  mutation CreateUserLogin($userId: ID!) {
    createUserLogin(userId: $userId) {
      id
      loginAt
      userId
    }
  }
`);

export function CurrentUserProvider(props: PropsWithChildren<unknown>) {
  const { data, loading, error, refetch } = useQuery<GetUserQuery, GetUserQueryVariables>(
    GET_USER_QUERY
  );
  const [hasInitialLogin, setHasInitialLogin] = useState(false);

  const [createUserLogin] = useMutation(CREATE_USER_LOGIN);

  const contextValue = useMemo(
    () => ({
      user: data?.getCurrentUser,
      loading,
      refetchUser: refetch,
      originalUserType: data?.getCurrentUser?.originalUserType,
    }),
    [data?.getCurrentUser, loading, refetch]
  );

  useEffect(() => {
    const handlePostAuthLogin = async (id: string) => {
      try {
        await createUserLogin({
          variables: {
            userId: id,
          },
        });
        setHasInitialLogin(true);
      } catch (err) {
        console.error("Failed to create post-auth userLogin record:", err);
      }
    };

    if (loading === false && hasInitialLogin === false && contextValue?.user?.id) {
      handlePostAuthLogin(contextValue.user.id);
    }
  }, [loading, hasInitialLogin, createUserLogin, contextValue?.user?.id]);

  // Persist last successful SSO login (email + provider) after auth completes
  useEffect(() => {
    if (loading) return;
    const user = contextValue?.user;
    if (!user) return;

    try {
      let ssoProvider: string | undefined;
      if (isEncampSSOEmail(user.email) && checkIsOriginalUserStaff(user)) {
        ssoProvider = ENCAMP_SSO_PROVIDER;
      } else {
        const emailDomain = user.email.split("@").pop()?.toLowerCase();
        if (emailDomain) {
          const userTenants = contextValue?.user?.UserTenant;

          const domainsMatch = (domains?: string[] | null): boolean => {
            if (!domains) return false;
            if (Array.isArray(domains)) {
              return domains.map((d) => d.toLowerCase()).includes(emailDomain);
            }
            return false;
          };

          for (const ut of userTenants || []) {
            const t = ut.tenant;
            if (!t) {
              continue;
            }
            if (domainsMatch(t.ssoDomains) && t.ssoMappingName) {
              ssoProvider = t.ssoMappingName;
              break;
            }
            const pt = t.partnerTenant;
            if (pt && domainsMatch(pt.ssoDomains) && pt.ssoMappingName) {
              ssoProvider = pt.ssoMappingName;
              break;
            }
          }
        }
      }

      if (ssoProvider) {
        const lastSsoLogin: LastSsoLogin = {
          email: user.email.toLowerCase(),
          provider: ssoProvider,
        };
        localStorage.setItem(LAST_SSO_LOGIN_KEY, JSON.stringify(lastSsoLogin));
      } else {
        localStorage.removeItem(LAST_SSO_LOGIN_KEY);
      }
    } catch (err) {
      console.error("Failed to persist last successful SSO login:", err);
    }
  }, [loading, contextValue?.user]);

  if (error) {
    console.error(error);

    // Check if this is an authentication error (401)
    const isAuthError =
      error.networkError &&
      "statusCode" in error.networkError &&
      error.networkError.statusCode === 401;

    const hasUnauthenticatedError = error.graphQLErrors?.some(
      (err) => err.extensions?.code === "UNAUTHENTICATED"
    );

    // If it's an authentication error, sign out properly and redirect
    if (isAuthError || hasUnauthenticatedError) {
      // Mark that we're logging out to prevent redirect loops
      localStorage.setItem("logout", "true");

      // Sign out from AWS Amplify to clear all authentication state
      signOutAuth()
        .then(() => {
          // Force a full page reload to clear any cached state
          window.location.href = "/";
        })
        .catch((signOutError) => {
          console.error("Error during signout:", signOutError);
          // Even if signout fails, still redirect to clear the app state
          window.location.href = "/";
        });

      // Return a loading state while the redirect happens
      return (
        <currentUserContext.Provider
          value={{
            user: undefined,
            loading: true,
            refetchUser: refetch,
          }}
        >
          {props.children}
        </currentUserContext.Provider>
      );
    }

    // For other errors, throw as before
    throw error;
  }

  const partnerTenant = data?.getCurrentUser?.UserTenant.find((ut) => ut.tenant?.partnerTenant)
    ?.tenant?.partnerTenant;

  // Just grab the first non-null partner tenant -- by convention, there should only be one
  const { name: partnerTenantName, id: partnerTenantId } = partnerTenant || {};

  // Just grab the first  tenant -- by convention, there should only be one
  const tenant = data?.getCurrentUser?.UserTenant[0]?.tenant;
  const { name: tenantName, id: tenantId } = tenant || {};

  configureSentryUser({
    user: data?.getCurrentUser ? { ...data.getCurrentUser } : null,
    tenantName,
    partnerTenantName,
    tenantId,
    partnerTenantId,
  });

  return (
    <currentUserContext.Provider value={contextValue}>{props.children}</currentUserContext.Provider>
  );
}
